/**
 * GlobalAnnouncementBar — professional single-line marquee ticker (Jun 2026).
 *
 * Reads ONE endpoint (/unified-announcements/my/channels) that merges the
 * legacy global bar with all active "urgent" + "major" unified announcements
 * for the current user.
 *
 * Display rules (user-specified):
 *  * Text renders on ONE line sized to the viewer's screen; if longer, it
 *    auto-scrolls horizontally (marquee) to reveal the rest, then scrolls back.
 *  * No "اقرأ الإعلان كاملاً" helper line — if the announcement carries a
 *    link, its OWN label shows as a gold pill button instead.
 *  * The whole bar stays clickable → opens the full announcement modal.
 *
 * Dismissal is per-message id (localStorage). Publishing a new message
 * automatically resurfaces the bar. Expired items are filtered server-side.
 */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Megaphone, X, ArrowLeft, Sparkles, FileText, PartyPopper } from 'lucide-react';
import { useApiQuery } from '../hooks/useApiQuery';
import { useAuth } from '../contexts/AuthContext';
import { canRoleAccessLink } from '../lib/announcementLinks';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'mesp.announcement.dismissed.v2';
const GOLD = '#E5C158';
const MARQUEE_SPEED = 45;        // px per second
const ROTATE_BASE_MS = 8000;     // min time each message stays on screen

// Minimal rich text: **bold** segments render gold-bold. No HTML —
// injection-safe by construction.
const renderRich = (text) => {
  const parts = String(text || '').split(/\*\*(.+?)\*\*/g);
  return parts.map((seg, i) =>
    i % 2 === 1
      ? <strong key={i} style={{ color: GOLD }} className="font-bold">{seg}</strong>
      : <React.Fragment key={i}>{seg}</React.Fragment>
  );
};

const loadDismissed = () => {
  try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]')); }
  catch { return new Set(); }
};

export const GlobalAnnouncementBar = () => {
  const { user } = useAuth();
  const { data } = useApiQuery(
    ['announcements', 'channels', user?.id],
    '/unified-announcements/my/channels',
    { staleTime: 60 * 1000, silent: true },
  );

  const [dismissed, setDismissed] = React.useState(loadDismissed);
  const [idx, setIdx] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const [marquee, setMarquee] = React.useState(null); // {shift, dur} | null
  const trackRef = React.useRef(null);
  const textRef = React.useRef(null);
  const barRef = React.useRef(null);

  // Auto-close the modal on route change so its Radix overlay never
  // blocks page modals (e.g. classroom details).
  const { pathname } = useLocation();
  React.useEffect(() => { setOpen(false); }, [pathname]);

  const items = React.useMemo(
    () => (data?.ticker || []).filter((t) => !dismissed.has(t.id)),
    [data, dismissed],
  );

  // clamp index when items change
  React.useEffect(() => {
    if (idx >= items.length) setIdx(0);
  }, [items.length, idx]);

  const current = items[Math.min(idx, Math.max(items.length - 1, 0))];
  const currentId = current?.id;
  const hasItems = items.length > 0 && Boolean(current);

  // Publish the bar's LIVE height as a :root CSS variable so full-height
  // layouts (chat composer, sticky bottom bars) subtract it precisely —
  // `calc(100dvh - 64px - var(--mesp-announce-h, 0px))`. ResizeObserver
  // covers text wrap, message rotation, screen resize and dismissal.
  React.useEffect(() => {
    const root = document.documentElement;
    if (!hasItems) {
      root.style.setProperty('--mesp-announce-h', '0px');
      return undefined;
    }
    const el = barRef.current;
    if (!el) return undefined;
    const apply = () => root.style.setProperty('--mesp-announce-h', `${el.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty('--mesp-announce-h', '0px');
    };
  }, [hasItems, currentId]);

  // measure overflow → configure marquee for the current message
  React.useLayoutEffect(() => {
    setMarquee(null);
    if (!currentId) return undefined;
    const raf = requestAnimationFrame(() => {
      const track = trackRef.current;
      const text = textRef.current;
      if (!track || !text) return;
      const overflow = text.scrollWidth - track.clientWidth;
      if (overflow > 8) {
        setMarquee({ shift: overflow, dur: Math.max(overflow / MARQUEE_SPEED, 4) });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [currentId]);

  // auto-rotate — long messages get enough time for a full scroll cycle
  React.useEffect(() => {
    if (items.length < 2 || open) return undefined;
    const ms = marquee
      ? Math.max(ROTATE_BASE_MS, (marquee.dur * 2 + 3) * 1000)
      : ROTATE_BASE_MS;
    const tm = setTimeout(() => setIdx((i) => (i + 1) % items.length), ms);
    return () => clearTimeout(tm);
  }, [items.length, open, currentId, marquee]);

  if (items.length === 0 || !current) return null;
  const isMajor = current.priority === 'major';
  const isCelebration = current.priority === 'celebration';
  const showLink = current.link_url && canRoleAccessLink(current.link_url, user?.role);
  const isInternal = current.link_url && current.link_url.startsWith('/');

  const dismiss = (e) => {
    if (e) e.stopPropagation();
    const next = new Set(dismissed);
    next.add(current.id);
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
    setDismissed(next);
  };

  const openModal = () => setOpen(true);
  const onBarKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openModal();
    }
  };

  return (
    <>
      <div
        ref={barRef}
        dir="rtl"
        className="mesp-announce-bar relative overflow-hidden cursor-pointer select-none"
        style={{
          background: 'linear-gradient(90deg, #0A1128 0%, #12224A 55%, #0A1128 100%)',
          borderBottom: '1px solid rgba(229,193,88,0.45)',
        }}
        data-testid="global-announcement-bar"
        role="button"
        tabIndex={0}
        onClick={openModal}
        onKeyDown={onBarKeyDown}
        aria-label="افتح تفاصيل الإعلان كاملاً"
      >
        {/* gold hairline on top */}
        <div className="absolute inset-x-0 top-0 h-[2px]"
             style={{ background: 'linear-gradient(90deg, transparent, #E5C158 30%, #C99A2F 70%, transparent)' }} />
        <div className="max-w-screen-2xl mx-auto flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-2 sm:py-2.5">
          <span
            className="mesp-announce-pulse shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full"
            style={{ background: 'rgba(229,193,88,0.14)', border: '1px solid rgba(229,193,88,0.4)' }}
            aria-hidden="true"
          >
            {isCelebration
              ? <PartyPopper size={14} style={{ color: '#4ADE80' }} />
              : isMajor
                ? <Sparkles size={14} style={{ color: GOLD }} />
                : <Megaphone size={14} style={{ color: GOLD }} />}
          </span>

          {/* single-line marquee track */}
          <div ref={trackRef} className="flex-1 min-w-0 overflow-hidden" data-testid="announcement-marquee-track">
            <div key={current.id} className="mesp-ticker-item">
              <p
                ref={textRef}
                className={`inline-block whitespace-nowrap text-xs sm:text-sm leading-snug text-white/95 ${marquee ? 'mesp-marquee-run' : ''}`}
                style={marquee ? { '--mesp-shift': `${marquee.shift}px`, '--mesp-dur': `${marquee.dur}s` } : undefined}
                data-testid="announcement-message"
              >
                {current.title ? <strong style={{ color: GOLD }} className="font-bold">{current.title}: </strong> : null}
                {renderRich(current.body)}
              </p>
            </div>
          </div>

          {/* link pill — shows the announcement's OWN label */}
          {showLink && (
            <a
              href={current.link_url}
              {...(isInternal ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 hidden xs:inline-flex sm:inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold transition-transform hover:-translate-y-0.5"
              style={{ background: `linear-gradient(135deg, ${GOLD}, #C99A2F)`, color: '#0A1128', boxShadow: '0 3px 10px rgba(201,154,47,0.35)' }}
              data-testid="announcement-link-pill"
            >
              {current.link_label || 'فتح الرابط'}
              <ArrowLeft size={11} />
            </a>
          )}

          {/* rotation dots (multi-message) */}
          {items.length > 1 && (
            <div className="shrink-0 hidden sm:flex items-center gap-1.5" data-testid="announcement-ticker-dots">
              {items.map((t, i) => (
                <button
                  key={t.id}
                  onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                  className="rounded-full transition-all"
                  style={{
                    width: i === idx ? 16 : 6, height: 6,
                    background: i === idx ? GOLD : 'rgba(255,255,255,0.3)',
                  }}
                  aria-label={`إعلان ${i + 1}`}
                />
              ))}
            </div>
          )}
          {items.length > 1 && (
            <span className="shrink-0 sm:hidden text-[10px] font-bold tabular-nums" style={{ color: GOLD }}>
              {idx + 1}/{items.length}
            </span>
          )}

          <button
            onClick={dismiss}
            className="shrink-0 p-1.5 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="إغلاق الإعلان"
            data-testid="announcement-close-btn"
          >
            <X size={16} />
          </button>
        </div>
        <style>{`
          .mesp-announce-bar { animation: mesp-announce-in .45s cubic-bezier(.22,1,.36,1) both; }
          @keyframes mesp-announce-in {
            from { transform: translateY(-100%); opacity: 0; }
            to   { transform: translateY(0);     opacity: 1; }
          }
          .mesp-ticker-item { animation: mesp-ticker-slide .5s cubic-bezier(.22,1,.36,1) both; }
          @keyframes mesp-ticker-slide {
            from { transform: translateY(70%); opacity: 0; }
            to   { transform: translateY(0);   opacity: 1; }
          }
          .mesp-announce-pulse { animation: mesp-announce-ring 2.2s ease-out infinite; }
          @keyframes mesp-announce-ring {
            0%   { box-shadow: 0 0 0 0 rgba(229,193,88,0.45); }
            70%  { box-shadow: 0 0 0 9px rgba(229,193,88,0); }
            100% { box-shadow: 0 0 0 0 rgba(229,193,88,0); }
          }
          /* Horizontal marquee — RTL: hidden text sits at the LEFT edge, so
             we translate the line RIGHT (+X) to reveal it, pause, and glide
             back. Pauses on hover so the reader can catch up. */
          .mesp-marquee-run {
            animation: mesp-marquee var(--mesp-dur) linear 1.6s infinite alternate;
            will-change: transform;
          }
          .mesp-announce-bar:hover .mesp-marquee-run { animation-play-state: paused; }
          @keyframes mesp-marquee {
            0%   { transform: translateX(0); }
            8%   { transform: translateX(0); }
            100% { transform: translateX(var(--mesp-shift)); }
          }
          @media (prefers-reduced-motion: reduce) {
            .mesp-announce-bar, .mesp-ticker-item, .mesp-announce-pulse, .mesp-marquee-run { animation: none; }
          }
        `}</style>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          dir="rtl"
          data-testid="announcement-detail-modal"
          className="max-w-lg border-[rgba(229,193,88,0.35)]"
          style={{
            background: 'linear-gradient(180deg, #0A1128 0%, #12224A 100%)',
            color: 'white',
          }}
        >
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <span
                className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full"
                style={{ background: 'rgba(229,193,88,0.14)', border: '1px solid rgba(229,193,88,0.4)' }}
                aria-hidden="true"
              >
                {isCelebration
                  ? <PartyPopper size={16} style={{ color: '#4ADE80' }} />
                  : isMajor
                    ? <Sparkles size={16} style={{ color: GOLD }} />
                    : <Megaphone size={16} style={{ color: GOLD }} />}
              </span>
              <DialogTitle
                className="text-right text-base sm:text-lg"
                style={{ color: GOLD }}
                data-testid="announcement-detail-title"
              >
                {current.title || 'إعلان المنصة'}
              </DialogTitle>
            </div>
          </DialogHeader>

          {current.attachment_url && current.attachment_kind === 'image' && (
            <img
              src={`${API_URL}${current.attachment_url}`}
              alt="صورة الإعلان"
              className="w-full max-h-72 object-contain rounded-xl mb-1"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(229,193,88,0.3)' }}
              data-testid="announcement-detail-image"
            />
          )}

          <p
            className="text-sm sm:text-base leading-relaxed text-white/95 whitespace-pre-wrap break-words"
            data-testid="announcement-detail-message"
          >
            {renderRich(current.body)}
          </p>

          {current.attachment_url && current.attachment_kind === 'pdf' && (
            <a
              href={`${API_URL}${current.attachment_url}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold w-fit"
              style={{ background: 'rgba(239,68,68,0.15)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.35)' }}
              data-testid="announcement-detail-pdf"
            >
              <FileText size={14} /> عرض الملف المرفق (PDF)
            </a>
          )}

          <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-start gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white/80 hover:text-white hover:bg-white/10 transition-colors border border-white/15"
              data-testid="announcement-detail-close-btn"
            >
              إغلاق
            </button>
            {showLink && (
              <a
                href={current.link_url}
                {...(isInternal ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center gap-1 px-4 py-2 rounded-lg text-sm font-bold transition-colors"
                style={{
                  background: 'linear-gradient(90deg, #E5C158, #C99A2F)',
                  color: '#0A1128',
                }}
                data-testid="announcement-detail-link"
              >
                {current.link_label || 'فتح الرابط'}
                <ArrowLeft size={14} />
              </a>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GlobalAnnouncementBar;
